import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Car, ClipboardList } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { dashboardLabels } from "@/i18n";
import { useMyObjectsAttention } from "@/hooks/useMyObjectsAttention";

export type ListingsOrdersTab = "my-auctions" | "orders";

const TAB_PATHS: Record<ListingsOrdersTab, string> = {
  "my-auctions": "/my-auctions",
  orders: "/orders",
};

const MIN_SWIPE_PX = 70;

function isInHorizontalScroller(el: Element | null): boolean {
  let node: Element | null = el;
  while (node && node !== document.body) {
    if (node instanceof HTMLElement) {
      if (node.dataset.swipeIgnore !== undefined) return true;
      const tag = node.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (node.getAttribute("role") === "dialog") return true;
      const overflowX = window.getComputedStyle(node).overflowX;
      if (
        (overflowX === "auto" || overflowX === "scroll") &&
        node.scrollWidth > node.clientWidth + 2
      ) {
        return true;
      }
    }
    node = node.parentElement;
  }
  return false;
}

type Props = {
  active: ListingsOrdersTab;
  className?: string;
};

/**
 * Top tabs for the merged "Mina annonser | Ordrar" hub.
 * Each tab keeps its own route (/my-auctions, /orders) so deep links and
 * notifications keep working. On mobile, swiping horizontally switches tab.
 */
export function ListingsOrdersTabs({ active, className }: Props) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isMobile = useIsMobile();
  const d = dashboardLabels(language);
  const { sellerNeedsAction, buyerNeedsAction } = useMyObjectsAttention();
  const start = useRef<{ x: number; y: number; ignore: boolean } | null>(null);

  const go = (tab: ListingsOrdersTab) => {
    if (tab === active) return;
    navigate(TAB_PATHS[tab], { replace: true });
  };

  useEffect(() => {
    if (!isMobile) return;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        start.current = null;
        return;
      }
      const t = e.touches[0];
      start.current = {
        x: t.clientX,
        y: t.clientY,
        ignore: isInHorizontalScroller(e.target as Element | null),
      };
    };

    const onEnd = (e: TouchEvent) => {
      const s = start.current;
      start.current = null;
      if (!s || s.ignore) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) < MIN_SWIPE_PX || Math.abs(dx) < Math.abs(dy) * 2) return;
      if (dx < 0 && active === "my-auctions") {
        navigate(TAB_PATHS.orders, { replace: true });
      } else if (dx > 0 && active === "orders") {
        navigate(TAB_PATHS["my-auctions"], { replace: true });
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [isMobile, active, navigate]);

  const tabs: {
    value: ListingsOrdersTab;
    label: string;
    icon: typeof Car;
    attention?: boolean;
  }[] = [
    {
      value: "my-auctions",
      label: d.myAuctions.title,
      icon: Car,
      attention: sellerNeedsAction,
    },
    {
      value: "orders",
      label: d.orders.title,
      icon: ClipboardList,
      attention: buyerNeedsAction,
    },
  ];

  return (
    <div
      role="tablist"
      className={cn(
        "grid grid-cols-2 border-b border-border mb-4",
        className,
      )}
    >
      {tabs.map(({ value, label, icon: Icon, attention }) => {
        const isActive = value === active;
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => go(value)}
            className={cn(
              "relative flex items-center justify-center gap-2 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors",
              isActive
                ? "border-accent text-accent"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            {attention ? (
              <span
                className="absolute top-2 right-3 h-2 w-2 rounded-full bg-red-500"
                aria-hidden
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
